/**
 * A recording from the microphone, turned into the one file the chat's voice slot accepts: AAC in
 * an MP4 container (`audio/mp4`, `.m4a`).
 *
 * ── Why the browser re-encodes ───────────────────────────────────────────────────────────────
 * `MediaRecorder` in Chrome and Firefox writes WebM/Opus, and the backend's voice class takes
 * `audio/mp4` only: the slot refuses any other type, and the ingest forces the MP4 demuxer, so a
 * WebM file labelled as MP4 would fail there instead. Safari records MP4 already, and the same
 * path re-encodes it too rather than branch on a container nobody can see — a voice note is a few
 * hundred kilobytes, and one route through the code is one route to test.
 *
 * Mono at 64 kbps, the phone's own profile and the bitrate the backend transcodes to when it has
 * to. The work runs in `encodeVoice.worker.ts`: where the browser has no AAC encoder (Firefox,
 * Chromium on Linux) the WebAssembly one is compiled there, which the page's own policy refuses.
 */

import { VOICE_CLASS } from './voiceClass';
import type { EncodeVoiceOutcome, EncodeVoiceRequest } from './encodeVoice.worker';

export { VOICE_CLASS };

export type VoiceEncodeOutcome =
  | { readonly ok: true; readonly file: File }
  | { readonly ok: false; readonly refusal: 'undecodable' | 'unsupportedBrowser' | 'tooLarge' };

export function encodeVoiceNote(recording: Blob): Promise<VoiceEncodeOutcome> {
  return new Promise((resolve) => {
    let worker: Worker;
    try {
      worker = new Worker(new URL('./encodeVoice.worker.ts', import.meta.url), { type: 'module' });
    } catch {
      resolve({ ok: false, refusal: 'unsupportedBrowser' });
      return;
    }
    const finish = (outcome: VoiceEncodeOutcome) => {
      worker.terminate();
      resolve(outcome);
    };
    worker.addEventListener('message', (event: MessageEvent<EncodeVoiceOutcome>) => {
      const outcome = event.data;
      finish(
        outcome.ok
          ? {
              ok: true,
              file: new File([outcome.buffer], 'wiadomosc-glosowa.m4a', { type: VOICE_CLASS.contentType }),
            }
          : outcome,
      );
    });
    // A worker that fails to load never answers; say so rather than wait.
    worker.addEventListener('error', () => {
      finish({ ok: false, refusal: 'unsupportedBrowser' });
    });
    const request: EncodeVoiceRequest = { recording };
    worker.postMessage(request);
  });
}
